<template>
  <div
    ref="dropdownMenu"
    class="rs-dropdown"
  >
    <RsButton
      v-bind="$attrs"
      class="dropdown-toggle"
      :variant="variant"
      :class="btnClasses"
      @click="toggleVisible"
    >
      <slot name="btn-content">
        <RsIcon
          v-if="!!btnIcon"
          :icon="btnIcon"
        />
        {{ title }}
      </slot>
    </RsButton>
    <Transition name="rs-dropdown-menu">
      <div
        v-show="isVisible"
        v-click-outside="setInvisible"
        class="rs-dropdown__menu"
        :class="dropdownClasses"
      >
        <RsDropdownItem
          v-for="(option, i) in options"
          :key="option.id || i"
          :title="option.label || title"
          :icon="option.icon"
          :href="option.href"
          @click="
            emitTo(
              { ...option },
              {
                emitToComponent: option.emitToComponent,
                eventName: option.eventName,
              },
            )
          "
        />
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IPropsClickConfig } from '@/shared';

type tDropdownClasses = {
  show: Boolean;
  'rs-dropdown--right': Boolean;
  'rs-dropdown--up': Boolean;
};

type tButtonClasses = {
  'no-caret': Boolean;
  'rs-dropdown--active': Boolean;
  'rs-dropdown--action-menu': Boolean;
  'rs-dropdown--small': Boolean;
  'rs-dropdown--alt': Boolean;
};

export default defineComponent({
  name: 'RsDropdown',

  props: {
    /**
     * Color variants: primary | secondary | success | warning | error | info | dark | light
     *
     */
    variant: {
      type: String,
      validator: (prop?: string) => [
        'primary',
        'secondary',
        'success',
        'warning',
        'error',
        'info',
        'secondary-alt',
        'dark',
        'light',
        'cancel',
      ].includes(prop ?? 'primary'),
      default: 'primary',
    },

    /**
     * Button text.
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Icon for the button.
     */
    btnIcon: {
      type: String,
      default: '',
    },

    /**
     * href | icon | title
     *
     */
    options: {
      type: Array as PropType<IPropsClickConfig[]>,
      default: () => [],
    },
    /**
     * Reduces the size of the button.
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Adds large alternate style suitable for select areas.
     */
    alt: {
      type: Boolean,
      default: false,
    },
    /**
     * Align the right edge of the menu with the right side of the button.
     */
    right: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the dropdown is INITIALLY visible, should always be false...
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Positions the menu on the top of the button.
     */
    dropup: {
      type: Boolean,
      default: false,
    },
    /**
     * Hide the caret indicator on the toggle button.
     */
    noCaret: {
      type: Boolean,
      default: false,
    },

    actionMenu: {
      type: Boolean,
      default: false,
    },

    /**
     * For Global Event Bus
     */
    dropdownEventName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isVisible: this.visible,
      visiblityKey: 0,
    };
  },

  computed: {
    btnClasses(): tButtonClasses {
      return {
        'no-caret': this.noCaret,
        'rs-dropdown--action-menu': this.actionMenu,
        'rs-dropdown--active': this.isVisible,
        'rs-dropdown--small': this.small,
        'rs-dropdown--alt': this.alt,
      };
    },
    dropdownClasses(): tDropdownClasses {
      return {
        show: this.isVisible,
        'rs-dropdown--right': this.right,
        'rs-dropdown--up': this.dropup,
      };
    },
  },

  watch: {
    visible(newVal, oldVal) {
      if (this.isVisible && this.visible === false) {
        this.toggleVisible();
      }
    },
  },

  methods: {
    documentClick(e: Event) {
      const el = this.$refs.dropdownMenu;
      const { target } = e;

      if (el !== target) this.isVisible = false;
    },
    toggleVisible(e?: Event) {
      this.isVisible = !this.isVisible;
      this.handleDropdownChanged({ label: '', icon: '', data: this.isVisible });
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    setInvisible() {
      this.isVisible = false;
      this.handleDropdownChanged({ label: '', icon: '', data: this.isVisible });
    },

    handleDropdownChanged(data: IPropsClickConfig) {
      this.$emit('dropdown-changed', { ...data });
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.rs-dropdown {
  position: relative;
  &__menu {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 10rem;
    margin: 0;
    padding: 0.5rem;
    z-index: 20;
    background-color: $white;
    box-shadow: 0 0px 10px 2px rgba(0, 0, 0, 0.1);
    border-radius: $global-radius;
  }
  .dropdown-toggle {
    &:after {
      transition: transform 0.2s ease;
    }
    &.rs-dropdown--active,
    &:hover {
      &:after {
        transform: rotate(180deg);
      }
    }
    &.no-caret {
      &:after {
        display: none;
      }
    }
  }
  &--small {
    padding: 0.5rem;
    height: 2.2rem;
    font-size: 0.875rem;
    font-weight: 600;
  }
  &--right {
    left: auto;
    right: 0;
  }
  &--up {
    top: auto;
    bottom: 100%;
  }
  &--up.rs-dropdown--up {
    left: 45px;
    right: auto;
    bottom: 0;
  }
  &--action-menu {
    padding: 0.5rem 1rem;
    width: auto;
    height: auto;
    border: none;
    font-size: 1.5rem;
    color: $tertiary-gray;
    background-color: transparent;
    &:hover {
      background-color: transparent;
    }
    i {
      margin: 0;
      width: auto !important;
    }
    &:after {
      display: none;
    }
  }
  &--alt {
    padding: 0.5rem 1rem;
    text-align: left;
    width: 100%;
    justify-content: start;
    height: 56px;
    border: $input-border;
    border-radius: 3px;
    font-size: 1rem;
    background-color: white;
    font-weight: 400;
    font-size: 1rem;
    color: $dark-gray;
    + .rs-dropdown__menu {
      min-width: 100%;
    }
    .rs-icon {
      margin-right: 1.5rem;
      font-size: 1rem;
      color: $secondary-color;
    }
    &:hover {
      border-color: $tertiary-gray;
      background-color: #efefef;
    }
    &:after {
      position: absolute;
      right: 1rem;
      color: $secondary-color;
    }
    .input-group {
      .input-group-prepend {
        .input-group-text {
          padding: 0.375rem 1rem;
          border-right: 0;
          color: $secondary-color;
        }
      }
    }
  }
}

// Vue 3 <Transition> classes replacing the one-way slideInDown/slideInUp animations —
// same 0.25s translate/opacity motion, but also animating the close direction.
.rs-dropdown-menu-enter-active,
.rs-dropdown-menu-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.rs-dropdown-menu-enter-from,
.rs-dropdown-menu-leave-to {
  opacity: 0;
  transform: translateY(-1rem);
}
.rs-dropdown--up.rs-dropdown-menu-enter-from,
.rs-dropdown--up.rs-dropdown-menu-leave-to {
  transform: translateY(1rem);
}

@keyframes slideInDown {
  0% {
    transform: translateY(-1rem);
    opacity: 0;
  }
  100% {
    transform: translateY(0rem);
    opacity: 1;
  }
  0% {
    transform: translateY(-1rem);
    opacity: 0;
  }
}
@keyframes slideInUp {
  0% {
    transform: translateY(1rem);
    opacity: 0;
  }
  100% {
    transform: translateY(0rem);
    opacity: 1;
  }
  0% {
    transform: translateY(1rem);
    opacity: 0;
  }
}
</style>
